import React, { useState } from 'react';
import { sendTestNotification } from '../utils/api';

export function NotificationsPanel() {
  const [email, setEmail] = useState('');
  const [chatId, setChatId] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [telegramMsg, setTelegramMsg] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(false);

  async function testEmail() {
    setEmailLoading(true);
    setEmailMsg('');
    try {
      const res = await sendTestNotification('email', email);
      setEmailMsg(res.message);
    } catch (err) {
      setEmailMsg(err instanceof Error ? err.message : 'Error sending test email.');
    } finally {
      setEmailLoading(false);
    }
  }

  async function testTelegram() {
    setTelegramLoading(true);
    setTelegramMsg('');
    try {
      const res = await sendTestNotification('telegram', undefined, chatId);
      setTelegramMsg(res.message);
    } catch (err) {
      setTelegramMsg(err instanceof Error ? err.message : 'Error sending test message.');
    } finally {
      setTelegramLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.warning}>
        <strong>Privacy notice:</strong> We store only what's necessary to send notifications.
        Never share your email password here — only use app-specific passwords.
      </div>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Email Notifications</h3>
        <p style={styles.desc}>
          Enter your email address to test. You must configure <code>EMAIL_SMTP_*</code> environment
          variables in the backend for email to work.
        </p>
        <div style={styles.row}>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
          />
          <button style={styles.testBtn} onClick={testEmail} disabled={emailLoading || !email.trim()}>
            {emailLoading ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>
        {emailMsg && <p style={msgStyle(emailMsg)}>{emailMsg}</p>}
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Telegram Notifications</h3>
        <p style={styles.desc}>
          Enter your Telegram chat ID to test. You must configure <code>TELEGRAM_BOT_TOKEN</code> in the backend.
          Get your chat ID by messaging <strong>@userinfobot</strong> on Telegram.
        </p>
        <div style={styles.row}>
          <input
            type="text"
            placeholder="e.g. 123456789"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            style={styles.input}
          />
          <button style={styles.testBtn} onClick={testTelegram} disabled={telegramLoading || !chatId.trim()}>
            {telegramLoading ? 'Sending...' : 'Send Test Message'}
          </button>
        </div>
        {telegramMsg && <p style={msgStyle(telegramMsg)}>{telegramMsg}</p>}
      </section>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>How Notifications Work</h3>
        <ul style={styles.list}>
          <li>Notifications are checked automatically every 2 hours by the backend scheduler.</li>
          <li>Alerts fire when: a new listing appears, status changes to Available/Coming Soon, or price drops below your target.</li>
          <li>Rate-limited to a maximum of one notification per product every 6 hours.</li>
          <li>Credentials are never exposed to the frontend — all sending happens server-side.</li>
        </ul>
      </section>
    </div>
  );
}

function msgStyle(msg: string): React.CSSProperties {
  const isError = msg.toLowerCase().includes('fail') || msg.toLowerCase().includes('error');
  return {
    fontSize: '13px',
    padding: '8px 12px',
    borderRadius: '6px',
    marginTop: '8px',
    background: isError ? '#ef444420' : '#22c55e20',
    color: isError ? '#ef4444' : '#22c55e',
    border: `1px solid ${isError ? '#ef4444' : '#22c55e'}`,
  };
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '24px' },
  warning: {
    padding: '12px 16px', background: '#eab30820', border: '1px solid #eab308',
    borderRadius: '8px', fontSize: '13px', color: '#eab308',
  },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  sectionTitle: { fontSize: '16px', fontWeight: 700 },
  desc: { fontSize: '13px', color: '#8a93b0', lineHeight: 1.6 },
  row: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  input: {
    flex: 1, minWidth: '200px', padding: '10px 14px',
    background: '#1a1d27', border: '1.5px solid #2e3347',
    borderRadius: '8px', color: '#e8eaf0', fontSize: '14px', outline: 'none',
  },
  testBtn: {
    padding: '10px 18px', background: '#f97316', border: 'none',
    borderRadius: '8px', color: '#fff', fontWeight: 700, cursor: 'pointer',
    fontSize: '14px', whiteSpace: 'nowrap' as const,
  },
  list: { paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#8a93b0', lineHeight: 1.6 },
};
